import React, { useRef, ReactNode, useState } from 'react'
import Slider, { Settings } from 'react-slick'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleLeft,
  faAngleRight,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons'
import { CarouselSlide, CarouselSlideProps } from './CarouselSlide'
import { onButtonTriggerKey } from '../../utils/keyboard'
import useBreakpoint from '../../hooks/useBreakpoint'
import BackgroundImage from '../Image/BackgroundImage'
import 'slick-carousel/slick/slick.css'
import './carousel.scss'

export type CarouselViewMore = {
  url?: string
  label?: string
  handleClick?: (e: React.MouseEvent | React.KeyboardEvent) => void
}

export type CustomTopControlButton = {
  label: string
  url?: string
  onClick: (e: React.MouseEvent | React.KeyboardEvent) => void
  icon?: string
}

export type FlexibleSlideConfig = CarouselSlideProps & {
  children?: ReactNode
  component?: React.ComponentType<any>
  componentProps?: Record<string, any>
  id?: string
  contentful_id?: string
  className?: string
  title?: string
  backgroundImage?: any
}

type ArrowsPosition = 'top' | 'overlay'
type DotsPosition = 'left' | 'middle' | 'right' | 'bottom'
type PagerType = 'dots' | 'tiles'

export type FlexibleCarouselProps = {
  children?: ReactNode
  modifiers?: string[]
  slides?: FlexibleSlideConfig[]
  title?: string
  arrows?: boolean
  arrowsPosition?: ArrowsPosition
  dots?: boolean
  dotsPosition?: DotsPosition
  pagerType?: PagerType
  useCardsLayout?: boolean
  divider?: boolean
  viewMore?: CarouselViewMore | null
  customTopControls?: CustomTopControlButton[]

  slidesToShow?: Settings['slidesToShow']
  slidesToScroll?: Settings['slidesToScroll']
  infinite?: Settings['infinite']
  lazyLoad?: Settings['lazyLoad']
  speed?: Settings['speed']
  autoplay?: Settings['autoplay']
  autoplaySpeed?: Settings['autoplaySpeed']
  fade?: Settings['fade']
  pauseOnHover?: Settings['pauseOnHover']
  adaptiveHeight?: Settings['adaptiveHeight']
  centerMode?: Settings['centerMode']
  centerPadding?: Settings['centerPadding']
  responsive?: Settings['responsive']
  onSlideChange?: (currentSlide: number) => void
  initialSlide?: number
}

// =============================================
// INTERNAL COMPONENTS
// =============================================

export const CarouselTopControls: React.FC<{
  title?: string
  customControls?: CustomTopControlButton[]
  onPrevious: () => void
  onNext: () => void
}> = ({ customControls, onPrevious, onNext }) => (
  <div className="fr-column fr-column--two fr-carousel-controls-wrapper">
    <div className="fr-column" />

    <div className="fr-column fr-ta--right">
      {customControls?.map((control, index) => (
        <a
          key={`ui-control-${control.label}-${control.url || index}`}
          className="fr-button fr-button--4 fr-button--small fr-button--pill"
          href={control.url || '#'}
          target="_self"
          tabIndex={0}
          onClick={control.onClick}
        >
          {control.label}
          {control.icon && (
            <i className={`${control.icon} fr-button--icon-right`} />
          )}
        </a>
      ))}

      {customControls && customControls.length > 0 && (
        <span className="fr-divider--1-v">|</span>
      )}

      <button
        type="button"
        className="fr-button fr-button--4 fr-button--small fr-button--pill fr-carousel-controls-top fr-carousel-controls-top--prev"
        onClick={onPrevious}
        onKeyDown={onButtonTriggerKey(onPrevious)}
        aria-label="Previous Slide"
      >
        <FontAwesomeIcon
          icon={faAngleLeft}
          className="fr-carousel-arrow-icon"
        />
      </button>
      <button
        type="button"
        className="fr-button fr-button--4 fr-button--small fr-button--pill fr-carousel-controls-top fr-carousel-controls-top--next"
        onClick={onNext}
        onKeyDown={onButtonTriggerKey(onNext)}
        aria-label="Next Slide"
      >
        <FontAwesomeIcon
          icon={faAngleRight}
          className="fr-carousel-arrow-icon"
        />
      </button>
    </div>
  </div>
)

export const CarouselOverlayControls: React.FC<{
  onPrevious: () => void
  onNext: () => void
}> = ({ onPrevious, onNext }) => (
  <>
    <button
      type="button"
      className="fr-carousel-controls fr-carousel-controls--prev"
      onClick={onPrevious}
      aria-label="Previous Slide"
    >
      <FontAwesomeIcon
        icon={faAngleLeft}
        className="fr-carousel-overlay-icon"
      />
    </button>
    <button
      type="button"
      className="fr-carousel-controls fr-carousel-controls--next"
      onClick={onNext}
      aria-label="Next Slide"
    >
      <FontAwesomeIcon
        icon={faAngleRight}
        className="fr-carousel-overlay-icon"
      />
    </button>
  </>
)

type SlickSettingsOptions = {
  restSlickSettings: any
  infinite: boolean
  speed: number
  slidesToShow: number
  slidesToScroll: number
  fade: boolean
  pauseOnHover: boolean
  adaptiveHeight: boolean
  centerMode: boolean
  centerPadding?: string
  initialSlide: number
  autoplay: boolean
  autoplaySpeed: number
  responsive?: Settings['responsive']
  totalSlides: number
  setCurrentSlide: (index: number) => void
  onSlideChange?: (currentSlide: number) => void
}

const getSlickSettings = (opts: SlickSettingsOptions): Settings => {
  const {
    restSlickSettings,
    infinite,
    speed,
    slidesToShow,
    slidesToScroll,
    fade,
    pauseOnHover,
    adaptiveHeight,
    centerMode,
    centerPadding,
    initialSlide,
    autoplay,
    autoplaySpeed,
    responsive,
    totalSlides,
    setCurrentSlide,
    onSlideChange
  } = opts

  return {
    ...restSlickSettings,
    accessibility: true,
    arrows: false,
    dots: false,
    infinite,
    speed,
    slidesToShow,
    slidesToScroll,
    fade: fade && slidesToShow === 1,
    pauseOnHover,
    adaptiveHeight,
    centerMode,
    centerPadding,
    initialSlide,
    autoplay: autoplay && totalSlides > 1,
    autoplaySpeed,
    lazyLoad: 'ondemand',
    responsive: responsive || [
      { breakpoint: 768, settings: { slidesToShow: 1, slidesToScroll: 1 } }
    ],
    afterChange: (index: number) => {
      setCurrentSlide(index)
      onSlideChange?.(index)
    }
  }
}

// Helper functions to reduce cognitive complexity
const renderViewMoreButton = (viewMore: CarouselViewMore) => (
  <a
    className="fr-button fr-button--4 fr-button--small fr-button--brick fr-more"
    href={viewMore.url || '#'}
    target="_self"
    tabIndex={0}
    onClick={viewMore.handleClick}
    data-testid="more-section-button"
  >
    {viewMore.label || 'View all'}
    <FontAwesomeIcon icon={faChevronRight} className="fr-button--icon-right" />
  </a>
)

const generateSlideKey = (
  slide: FlexibleSlideConfig,
  index: number
): string => {
  if (slide.contentful_id) {
    return `slide-${slide.contentful_id}`
  }

  return `slide-${index}`
}

/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-to-interactive-role */
const renderTilesPager = (
  slides: FlexibleSlideConfig[],
  currentSlide: number,
  dotsClasses: string,
  goToSlide: (slide: number) => void
) => {
  return (
    <ul className={classNames(dotsClasses, 'fr-slick-dots--tiles')}>
      {slides.map((slide, i) => {
        return (
          <li
            key={generateSlideKey(slide, i)}
            className={classNames(
              'fr-carousel-pager-item',
              i === currentSlide ? 'slick-active' : ''
            )}
          >
            <BackgroundImage
              classname="frw-va frw-bg--img frw-bg--blend"
              gatsbyImageData={slide.backgroundImage as any}
              Tag="div"
            >
              <div
                className="fr-carousel-pager-item-inner"
                onClick={() => goToSlide(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    goToSlide(i)
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Go to slide ${i + 1}: ${slide.title || ''}`}
                style={{ width: '100%', height: '100%', cursor: 'pointer' }}
              >
                <div className="fr-carousel-pager-title">{slide.title}</div>
              </div>
            </BackgroundImage>
          </li>
        )
      })}
    </ul>
  )
}

const renderDotsPager = (
  totalSlides: number,
  slidesToShow: number,
  slidesToScroll: number,
  infinite: boolean,
  currentSlide: number,
  dotsClasses: string,
  goToSlide: (slide: number) => void
) => {
  const dotCount = getDotCount(
    totalSlides,
    slidesToShow,
    slidesToScroll,
    infinite
  )
  return (
    <ul className={dotsClasses}>
      {Array.from({ length: dotCount }, (_, idx) => {
        // The slide this dot jumps to:
        const slideIdx = idx * slidesToScroll
        // Active if currentSlide is in this group:
        const isActive =
          currentSlide >= slideIdx && currentSlide < slideIdx + slidesToShow
        return (
          <li
            key={`dot-${idx}`}
            className={isActive ? 'slick-active' : ''}
            onClick={() => goToSlide(slideIdx)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                goToSlide(slideIdx)
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Go to slides ${slideIdx + 1} to ${Math.min(
              slideIdx + slidesToShow,
              totalSlides
            )}`}
            style={{ cursor: 'pointer' }}
          >
            <button
              type="button"
              aria-hidden="true"
              tabIndex={-1}
              style={{ pointerEvents: 'none' }}
            >
              {idx + 1}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-to-interactive-role */
function getDotCount(
  totalSlides: number,
  slidesToShow: number,
  slidesToScroll: number,
  infinite: boolean
) {
  if (infinite) {
    return Math.ceil(totalSlides / slidesToScroll)
  }
  return Math.ceil((totalSlides - slidesToShow) / slidesToScroll) + 1
}

// Helper function to render individual slide
const renderSingleSlide = (slideConfig: FlexibleSlideConfig, index: number) => {
  const {
    children: slideChildren,
    component: SlideComponent,
    componentProps = {},
    className,
    ...carouselSlideProps
  } = slideConfig

  const slideKey = generateSlideKey(slideConfig, index)

  return (
    <CarouselSlide
      key={slideKey}
      className={classNames('fr-carousel-slide', className)}
      {...carouselSlideProps}
    >
      {SlideComponent && (
        <SlideComponent {...componentProps} slideIndex={index} />
      )}
      {slideChildren}
    </CarouselSlide>
  )
}

// Helper function to render slides from config
const renderSlidesFromConfig = (slides: FlexibleSlideConfig[]) => {
  return slides.map((slideConfig, index) =>
    renderSingleSlide(slideConfig, index)
  )
}

const renderChildrenSlides = (children: ReactNode) => {
  return React.Children.map(children, (child, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <div key={`carousel-child-slide-${index}`} className="fr-carousel-slide">
      {child}
    </div>
  ))
}

// =============================================
// MAIN COMPONENT
// =============================================

function getDotsClasses(
  useCardsLayout: boolean,
  pagerType: string,
  dotsPosition: string
) {
  return classNames(
    'fr-slick-dots',
    useCardsLayout && pagerType === 'tiles' ? 'fr-slick-dots--cards' : '',
    `fr-ta--${dotsPosition}`
  )
}

function getPagerContent({
  pagerType,
  dots,
  totalSlides,
  slides,
  currentSlide,
  useCardsLayout,
  dotsPosition,
  hasEnoughSlides,
  slidesToShow,
  slidesToScroll,
  infinite,
  goToSlide
}: any) {
  if (pagerType === 'tiles' && dots && totalSlides > 0) {
    return renderTilesPager(
      slides,
      currentSlide,
      getDotsClasses(useCardsLayout, pagerType, dotsPosition),
      goToSlide
    )
  }
  if (dots && hasEnoughSlides) {
    return renderDotsPager(
      totalSlides,
      slidesToShow,
      slidesToScroll,
      infinite,
      currentSlide,
      getDotsClasses(useCardsLayout, pagerType, dotsPosition),
      goToSlide
    )
  }
  return null
}

function renderPagination({ pagerContent, divider, viewMore }: any) {
  if (!pagerContent && !divider && !viewMore) {
    return null
  }
  return (
    <div className="fr-carousel-pager-wrapper">
      {pagerContent}
      {divider && <div className="fr-divider" />}
      {viewMore && renderViewMoreButton(viewMore)}
    </div>
  )
}

function renderTopControls({
  arrowsPosition,
  arrows,
  showArrows,
  title,
  customTopControls,
  previous,
  next
}: any) {
  if (arrowsPosition !== 'top' || !arrows) {
    return null
  }

  if (!showArrows) {
    return <div className="fr-carousel-controls-placeholder" />
  }

  return (
    <CarouselTopControls
      title={title}
      customControls={customTopControls}
      onPrevious={previous}
      onNext={next}
    />
  )
}

// eslint-disable-next-line complexity
export const FlexibleCarousel = React.forwardRef<any, FlexibleCarouselProps>(
  (
    {
      children,
      modifiers = [],
      slides = [],
      title,
      arrows = true,
      arrowsPosition = 'top',
      dots = true,
      dotsPosition = 'left',
      pagerType = 'dots',
      useCardsLayout = false,
      divider = false,
      viewMore,
      customTopControls,
      slidesToShow = 1,
      slidesToScroll = 1,
      infinite = true,
      speed = 0,
      autoplay = false,
      autoplaySpeed = 0,
      fade = false,
      pauseOnHover = true,
      adaptiveHeight = false,
      centerMode = false,
      centerPadding,
      responsive,
      onSlideChange,
      initialSlide = 0,
      ...restSlickSettings
    },
    ref
  ) => {
    const sliderRef = useRef<Slider>(null)
    const [currentSlide, setCurrentSlide] = useState(initialSlide || 0)

    const totalSlides =
      slides.length > 0 ? slides.length : React.Children.count(children)

    const { isMobile } = useBreakpoint()

    // Determine the number of slides to show based on the breakpoint.
    const currentSlidesToShow = isMobile ? 1 : slidesToShow

    const hasEnoughSlides = totalSlides > currentSlidesToShow
    const isPerfectFit = totalSlides === currentSlidesToShow
    const showArrows = hasEnoughSlides
    const enableInfinite = infinite && hasEnoughSlides

    const next = () => sliderRef.current?.slickNext()
    const previous = () => sliderRef.current?.slickPrev()
    const goToSlide = (slide: number) => sliderRef.current?.slickGoTo(slide)

    React.useImperativeHandle(ref, () => ({
      next,
      previous,
      goToSlide,
      sliderRef: sliderRef.current
    }))

    function renderSlideContent() {
      if (slides.length > 0) {
        return renderSlidesFromConfig(slides)
      }
      return renderChildrenSlides(children)
    }

    const modifierClasses = modifiers.map((m) => `mode-${m}`)

    const pagerContent = getPagerContent({
      pagerType,
      dots,
      totalSlides,
      slides,
      currentSlide,
      useCardsLayout,
      dotsPosition,
      hasEnoughSlides: showArrows,
      slidesToShow: currentSlidesToShow,
      slidesToScroll,
      infinite: enableInfinite,
      goToSlide
    })

    const pagination = renderPagination({
      pagerContent,
      divider,
      viewMore
    })

    const slickSettings = getSlickSettings({
      restSlickSettings,
      infinite: enableInfinite,
      speed,
      slidesToShow: currentSlidesToShow,
      slidesToScroll,
      fade,
      pauseOnHover,
      adaptiveHeight,
      centerMode,
      centerPadding,
      initialSlide,
      autoplay,
      autoplaySpeed,
      responsive,
      totalSlides,
      setCurrentSlide,
      onSlideChange
    })

    return (
      <div
        className={classNames('fr-carousel-wrapper', ...modifierClasses, {
          'insufficient-slides': totalSlides < currentSlidesToShow,
          'perfect-fit': isPerfectFit,
          'has-multiple-slides': totalSlides > 1
        })}
        data-pager-type={pagerType}
      >
        {renderTopControls({
          arrowsPosition,
          arrows,
          showArrows,
          title,
          customTopControls,
          previous,
          next
        })}

        <div className="fr-carousel">
          <Slider ref={sliderRef} {...slickSettings}>
            {renderSlideContent()}
          </Slider>

          {arrows && arrowsPosition === 'overlay' && showArrows && (
            <CarouselOverlayControls onPrevious={previous} onNext={next} />
          )}
        </div>

        {pagination}
      </div>
    )
  }
)

FlexibleCarousel.displayName = 'FlexibleCarousel'
export default FlexibleCarousel
